import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldType } from '@ngx-formly/material';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import {
  compact,
  defaults,
  find,
  findIndex,
  forEach,
  get,
  includes,
  inRange,
  isObjectLike,
  map,
  pick,
  reduce,
  round,
  sortBy,
  sum,
  transform,
  values,
} from 'lodash';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { delay, merge, Observable, Subject, takeUntil, tap } from 'rxjs';

type FormValue = Record<string, SectionFormValue>;

interface SectionFormValue {
  _declaredValue: number;
  _fillWith: number;
  [key: string]: string | number;
}

interface Total {
  currency: number;
  percentage: number;
}

interface Field {
  id: string;
  label: string;
  currencyFieldLabel: string;
}

interface Table {
  description: string;
  tooltip: { text: string };
  fillWith: string;
  fields: Field[];
}

export interface Section {
  id: string;
  label: string;
  fieldPath: string;
  tooltip: { text: string };
  description: string;
  table: Table;
  fields: FormlyFieldConfig[];
  mismatch: number;
  summary: {
    label: string;
    breakdownLabel?: string;
  };
}

interface TotalRequirements {
  value: number;
  label: string;
  description: string;
  mismatch?: number;
}

interface InputProps extends FormlyFieldProps {
  sections: Section[];
  currency: string;
  totalLabel: string;
  currencyFieldLabel: string;
  percentageFieldLabel: string;
  fillWith: string;
  clearInputOnFocus: 'zero' | 'always';
  totalRequirements: TotalRequirements;
  readonly: boolean;
}

@Component({
    selector: 'lockton-apps-dynamic-tables-type',
    templateUrl: './dynamic-tables.component.html',
    styleUrls: ['./dynamic-tables.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DynamicTablesTypeComponent
  extends FieldType<FieldTypeConfig<InputProps>>
  implements OnInit, AfterViewInit, OnDestroy
{
  totals!: Total;
  mainForm!: FormGroup;
  percentageForm!: FormGroup;
  sections: Section[] = [];
  private destroyer$: Subject<void> = new Subject();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.prepareForms();
    this.setSections(map(this.props.sections, 'id'));
    this.trackFormChanges();
    this.listenToExternalChanges();
  }

  ngAfterViewInit(): void {
    this.formControl.setValue(this.flattenAdditionalData(this.mainForm.value));
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private trackFormChanges(): void {
    merge(this.mainForm.valueChanges, this.percentageForm.valueChanges)
      .pipe(takeUntil(this.destroyer$))
      .subscribe(() => {
        this.handleSectionFields();
        this.formControl.setValue(
          this.flattenAdditionalData(this.mainForm.value)
        );
        this.formControl.markAsDirty();
      });
  }

  private flattenAdditionalData(formValue: FormValue): FormValue {
    return transform(
      formValue,
      (result: any, value: any, key: string) => {
        if (isObjectLike(value)) {
          result[key] = transform(
            value,
            (_result: any, _value: any, _key: string) => {
              _key === 'additionalData'
                ? defaults(_result, _value)
                : (_result[_key] = _value);
            },
            {}
          );
        } else {
          result[key] = value;
        }
      },
      {}
    );
  }

  private calculateTotal(formValue: FormValue): number {
    return reduce(
      formValue,
      (declaredValue: number, sectionFormValue: SectionFormValue) => {
        declaredValue += sectionFormValue._declaredValue || 0;

        return declaredValue;
      },
      0
    );
  }

  private prepareForms(): void {
    this.mainForm = this.formBuilder.group({
      _fillWith: [
        get(this.model, `${this.field.key}._fillWith`, this.props.fillWith),
      ],
    });
    this.percentageForm = this.formBuilder.group({});

    this.formControl.setValidators(
      compact([
        this.invalidAncestorsValidator.bind(this),
        this.props.totalRequirements &&
          this.addUpRequiredAmountValidator.bind(this),
      ])
    );
  }

  private setSections(sections: string[]): void {
    this.sections = sortBy(
      map(sections, (id: string) => {
        const section: Section = find(this.props.sections, { id }) as Section;
        const sectionControl: FormGroup = this.mainForm.get(
          section.id
        ) as FormGroup;

        if (!sectionControl) {
          this.buildSectionControl(section);
        }

        section.fieldPath = `field_${this.id}.${section.id}`;

        return section;
      }),
      (section: Section) => findIndex(this.props.sections, section)
    ) as Section[];

    this.updateDeclaredValuesTotals();
  }

  private buildSectionControl(section: Section): void {
    this.setInternalFormSection(section);
    this.setPercentageFormSection(section);
    this.listenToSectionTotalChanges(section);
    this.listenToSectionFormChanges(section);
  }

  private setInternalFormSection(section: Section): void {
    this.mainForm.setControl(
      section.id,
      this.formBuilder.group(
        transform(
          section.table?.fields,
          (group: Record<string, any>, field: Field) => {
            group[field.id] = [
              get(this.model, `${this.field.key}.${section.id}.${field.id}`, 0),
              [Validators.required, Validators.min(0)],
            ];
          },
          {
            _declaredValue: [
              get(
                this.model,
                `${this.field.key}.${section.id}._declaredValue`,
                0
              ),
              [Validators.required, Validators.min(0)],
            ],
            _fillWith: [
              get(
                this.model,
                `${this.field.key}.${section.id}._fillWith`,
                section.table?.fillWith
              ),
            ],
          }
        ),
        {
          validators: [this.addUpTotalValidator.bind(this, section.id)],
        }
      )
    );

    if (section.fields) {
      forEach(section.fields, (field: FormlyFieldConfig) => {
        field.defaultValue = get(
          this.model,
          `${this.field.key}.${section.id}.${field.key}`,
          null
        );
        (field.props as FormlyFieldProps).readonly = this.props.readonly;
      });

      (this.mainForm.get(section.id) as FormGroup).setControl(
        'additionalData',
        new FormGroup({})
      );
    }
  }

  private setPercentageFormSection(section: Section): void {
    this.percentageForm.setControl(
      section.id,
      this.formBuilder.group(
        transform(
          section.table?.fields,
          (group: Record<string, any>, field: Field) => {
            const amount: number = get(
              this.model,
              `${this.field.key}.${section.id}.${field.id}`,
              0
            );
            const max: number = get(
              this.model,
              `${this.field.key}.${section.id}._declaredValue`,
              0
            );

            group[field.id] = [
              this.convertAmountToPercent(amount, max),
              [Validators.required, Validators.min(0), Validators.max(100)],
            ];
          },
          {
            _declaredValue: [
              this.convertAmountToPercent(
                this.mainForm.get(section.id)?.value._declaredValue,
                this.props.totalRequirements.value
              ),
            ],
          }
        )
      )
    );
  }

  private listenToSectionTotalChanges(section: Section): void {
    merge(
      this.mainForm.get(`${section.id}._declaredValue`)
        ?.valueChanges as Observable<number>,
      this.percentageForm.get(`${section.id}._declaredValue`)
        ?.valueChanges as Observable<number>
    )
      .pipe(
        takeUntil(this.destroyer$),
        tap((value: number) => {
          this.onSectionTotalChange(
            section,
            this.mainForm.get('_fillWith')?.value,
            value
          );
        }),
        delay(0)
      )
      .subscribe(this.updateDeclaredValuesTotals.bind(this));
  }

  private onSectionTotalChange(
    section: Section,
    fillWith: string,
    value: number
  ): void {
    switch (fillWith) {
      case 'currency':
        this.updateSectionTotal(
          this.percentageForm.get(
            `${section.id}._declaredValue`
          ) as AbstractControl,
          this.convertAmountToPercent(value, this.props.totalRequirements.value)
        );
        break;

      case 'percentage':
        this.updateSectionTotal(
          this.mainForm.get(`${section.id}._declaredValue`) as AbstractControl,
          this.convertPercentToAmount(value, this.props.totalRequirements.value)
        );
    }
  }

  private updateSectionTotal(target: AbstractControl, value: number): void {
    target.setValue(value, { emitEvent: false });
  }

  private updateDeclaredValuesTotals(): void {
    this.totals = {
      currency: this.calculateTotal(this.mainForm.value),
      percentage: this.calculateTotal(this.percentageForm.value),
    };

    this.cdr.markForCheck();
  }

  private listenToSectionFormChanges(section: Section): void {
    merge(
      this.mainForm.get(section.id)?.valueChanges as Observable<FormValue>,
      this.percentageForm.get(section.id)?.valueChanges as Observable<FormValue>
    )
      .pipe(takeUntil(this.destroyer$))
      .subscribe(this.onSectionFormChange.bind(this, section));
  }

  private onSectionFormChange(section: Section): void {
    const sectionFieldIds: string[] = map(
      find(this.props.sections, { id: section.id })?.table?.fields,
      'id'
    );

    forEach(
      (this.mainForm.get(section.id) as FormGroup).controls,
      (_, fieldId) => {
        if (includes(sectionFieldIds, fieldId)) {
          this.onFieldChange(this.mainForm.get(section.id)?.value._fillWith, [
            section.id,
            fieldId,
          ]);
        }
      }
    );
  }

  private handleSectionFields(): void {
    forEach(this.mainForm.controls, (control: AbstractControl, id: string) => {
      const section: Section = find(this.props.sections, { id }) as Section;
      const additionalDataControl: AbstractControl | null = this.mainForm.get(
        `${section?.id}.additionalData`
      );
      const hasDeclaredValueButNoControlSet: boolean =
        control.value._declaredValue && !additionalDataControl;
      const hasNoDeclaredValueButControlSet: boolean =
        !control.value._declaredValue && !!additionalDataControl;

      if (hasDeclaredValueButNoControlSet) {
        (this.mainForm.get(section.id) as FormGroup).setControl(
          'additionalData',
          new FormGroup({})
        );
      }

      if (hasNoDeclaredValueButControlSet) {
        (this.mainForm.get(section.id) as FormGroup).removeControl(
          'additionalData'
        );
      }
    });
  }

  private onFieldChange(fillWith: string, path: string[]): void {
    const fieldChangeEvent = this.getFieldChangeEvent(fillWith, path);

    fillWith === 'currency'
      ? this.onAmountChange(fieldChangeEvent)
      : this.onPercentChange(fieldChangeEvent);
  }

  private onAmountChange({
    origin,
    destination,
  }: Record<string, AbstractControl>): void {
    destination.setValue(
      this.convertAmountToPercent(
        origin.value,
        origin.parent?.value._declaredValue
      ),
      { emitEvent: false }
    );
  }

  private onPercentChange({
    origin,
    destination,
  }: Record<string, AbstractControl>): void {
    destination.setValue(
      this.convertPercentToAmount(
        origin.value,
        destination.parent?.value._declaredValue
      ),
      { emitEvent: false }
    );
  }

  private getFieldChangeEvent(
    fillWith: string,
    path: string[]
  ): Record<string, AbstractControl> {
    let origin: AbstractControl;
    let destination: AbstractControl;

    switch (fillWith) {
      case 'percentage':
        origin = this.percentageForm.get(path) as AbstractControl;
        destination = this.mainForm.get(path) as AbstractControl;
        break;

      case 'currency':
      default:
        origin = this.mainForm.get(path) as AbstractControl;
        destination = this.percentageForm.get(path) as AbstractControl;
    }

    return { origin, destination };
  }

  private convertPercentToAmount(value: number, max: number): number {
    return round((value * max) / 100, 6);
  }

  private convertAmountToPercent(value: number, max: number): number {
    return round((100 * value) / max, 6) || 0;
  }

  private addUpTotalValidator(
    id: string,
    control: FormGroup
  ): ValidationErrors | null {
    const section: Section = find(this.sections, { id }) as Section;

    if (!section?.table) {
      return null;
    }

    const sectionValue: SectionFormValue = control.value;
    const sectionFieldIds: string[] = map(section.table.fields, 'id');
    const total: number = sum(values(pick(sectionValue, sectionFieldIds)));

    return !sectionValue._declaredValue ||
      this.isInRange(total, sectionValue._declaredValue, section.mismatch)
      ? null
      : { mustAddUpTotal: true };
  }

  private addUpRequiredAmountValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    if (!this.props.totalRequirements) {
      return null;
    }

    return this.isInRange(
      this.calculateTotal(control.value),
      this.props.totalRequirements.value,
      this.props.totalRequirements.mismatch
    )
      ? null
      : { mustAddUpRequiredAmount: true };
  }

  private invalidAncestorsValidator(): ValidationErrors | null {
    return this.mainForm.invalid ? { invalidAncestors: true } : null;
  }

  private listenToExternalChanges(): void {
    if (!this.options.formState.validityCheck$) {
      return;
    }
    this.options.formState.validityCheck$
      .pipe(takeUntil(this.destroyer$))
      .subscribe(() => {
        this.mainForm.markAllAsTouched();
        this.percentageForm.markAllAsTouched();
      });
  }

  private isInRange(
    value: number,
    target: number,
    mismatch: number = 0
  ): boolean {
    return inRange(value, target - mismatch, target + mismatch);
  }
}
